/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import type { RosterManagementStudentWithGuardiansListItem } from '@whizz/api-model';
import type { Column } from 'react-table';
import { useI18n } from '@whizz/react-i18n';
import { Loader, Table } from '../../../../../components';
import { Edit } from './ParentsTable.components';
import { createGlobalStyle } from 'styled-components';
import NoData from '../../../../../global/sections/NoData';

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_active'],
    [data-column='cell_active'] {
      > div {
        display: flex;
        justify-content: center;
      }
    }
    [data-id='col_action'] {
      width: 0;
    }
    [data-id='col_name'] {
      width: 25%;
    }
    [data-id^='col_parent'],
    [data-id^='col_parent'] {
      width: 25%;
    }
  }
`;

interface Props {
  students: RosterManagementStudentWithGuardiansListItem[],
  onEdit: (student: RosterManagementStudentWithGuardiansListItem) => void;
  isLoading: boolean;
}

const ParentsTable = (
  {
    students,
    onEdit,
    isLoading,
  }: Props,
): JSX.Element => {
  const { translate } = useI18n();

  const columns: Column<RosterManagementStudentWithGuardiansListItem>[] = [
    {
      id: 'action',
      Header: () => '',
      accessor: 'id',
      Cell: ({ row }) => <Edit onClick={() => onEdit(row.original as RosterManagementStudentWithGuardiansListItem)} />,
      disableSortBy: true,
    },
    {
      id: 'name',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.PARENTS.TABLE.STUDENT_NAME'),
      accessor: (original) => `${(original.lastName ?? '')}###${original.firstName ?? ''}`,
      Cell: ({ value } : { value: string }) => {
        const nameArray = value.split('###');

        return `${nameArray[1]} ${nameArray[0]}`;
      },
    },
    {
      id: 'parent_1',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.PARENTS.TABLE.PARENT_1'),
      accessor: (original) => original.guardians?.[0]?.name ?? '',
      disableSortBy: true,
    },
    {
      id: 'parent_2',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.PARENTS.TABLE.PARENT_2'),
      accessor: (original) => original.guardians?.[1]?.name ?? '',
      disableSortBy: true,
    },
    {
      id: 'parent_3',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.PARENTS.TABLE.PARENT_3'),
      accessor: (original) => original.guardians?.[2]?.name ?? '',
      disableSortBy: true,
    },
  ];

  if (isLoading) {
    return (<Loader />);
  }

  return (
    <>
      <GlobalStyle />
      <div className={'border-b-2 border-gray-300 mb-4'}></div>
      <div className={'mb-32'}>
        <Table
          id={'parents'}
          cols={columns}
          content={students}
          noDataMessage={<NoData noDataMessage={translate('pages.ROSTER_MANAGEMENT.PARENTS.TABLE.NO_DATA')} />}
          initialState={{
            sortBy: [
              {
                id: 'name',
                desc: false,
              },
            ],
            pageSize: 25,
          }}
          disableSortRemove
        />
      </div>
    </>
  );
};

export default ParentsTable;
